import { Link } from 'react-router-dom';
import { INVESTMENT, PATH_SUMMARIES, CLOSING_STATEMENT } from '../lib/content';
import { Check, Users, Sparkles, HandHeart, ArrowDown } from 'lucide-react';

export default function Investment() {
  const fc = INVESTMENT.founding_cohort;

  return (
    <div data-testid="page-investment">
      {/* INTRO */}
      <section className="pt-28 md:pt-36 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-5">Investment · Community · Belonging</p>
          <h1 className="font-serif text-5xl md:text-6xl text-brand-brown leading-[1.05]">
            Your Therapeutic <em className="italic text-brand-brown-light">Journey</em>
          </h1>
          <p className="mt-7 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
            Nurture the Self is a year-long therapeutic pathway. This page sets out what participation includes — the shape of the work, the size of the group, the investment involved, and the care we take to hold each cohort well.
          </p>
        </div>
      </section>

      {/* THE THREE-PATH JOURNEY */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">The whole pathway</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">A three-part journey, unfolding over a year.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {PATH_SUMMARIES.map((p, i) => (
              <div key={p.id} data-testid={`journey-path-${i + 1}`} className="relative bg-brand-bone rounded-3xl p-7 border border-brand-brown/5">
                <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">{p.label}</p>
                <h3 className="font-serif text-2xl text-brand-brown leading-snug mb-3">{p.title}</h3>
                <p className="font-sans text-sm text-brand-brown-light/95 leading-relaxed">{p.summary}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col items-center gap-3">
            {['Nourish', 'Reconnect', 'Become'].map((stage, i, arr) => (
              <div key={stage} className="flex flex-col items-center">
                <span className="font-serif italic text-3xl md:text-4xl text-brand-gold tracking-wide">{stage}</span>
                {i < arr.length - 1 && (
                  <ArrowDown size={18} className="text-brand-sage-dark my-3" strokeWidth={1.5} />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDING COHORT INVESTMENT */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div data-testid="investment-founding-cohort" className="relative overflow-hidden rounded-3xl border border-brand-gold/40 bg-gradient-to-br from-brand-peach/50 via-brand-bg-soft to-brand-sage/50 p-8 md:p-12">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-brand-gold/10 blur-3xl" aria-hidden />
            <div className="relative">
              <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-gold mb-3">{fc.label}</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{fc.subtitle}</h2>

              <div className="mt-8 grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-start">
                <div className="md:border-r md:border-brand-brown/10 md:pr-12">
                  <span className="font-serif text-6xl md:text-7xl text-brand-brown leading-none">{fc.price}</span>
                  <p className="mt-3 font-sans text-xs uppercase tracking-[0.22em] text-brand-brown-muted">Founding Cohort · Path I</p>
                </div>
                <div className="space-y-4 font-sans text-base text-brand-brown-light/95 leading-relaxed">
                  <p>{fc.intro}</p>
                  <p>{fc.body}</p>
                  <p className="italic text-sm text-brand-brown-muted">{fc.future_note}</p>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-brand-brown/10">
                <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted mb-4">Payment options</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {fc.payment_options.map((opt, i) => (
                    <div key={i} className="bg-brand-bone rounded-2xl p-5 border border-brand-brown/5">
                      <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-brand-brown-muted">{i === 0 ? 'In full' : 'In instalments'}</p>
                      <p className="mt-1 font-serif text-2xl text-brand-brown">{opt}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-sans text-sm text-brand-brown-muted italic">{fc.payment_note}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-4">Included</p>
            <h2 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">What your investment holds</h2>
          </div>
          <div className="bg-brand-bone rounded-3xl p-8 md:p-10 border border-brand-brown/5">
            <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
              {INVESTMENT.includes.map((line, i) => (
                <li key={i} data-testid={`includes-${i}`} className="flex items-start gap-3 font-sans text-base text-brand-brown-light/95 leading-relaxed">
                  <Check size={18} className="text-brand-gold mt-0.5 shrink-0" strokeWidth={1.6} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY OUR GROUPS ARE SMALL */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div data-testid="investment-small-groups" className="relative overflow-hidden rounded-3xl bg-brand-sage p-10 md:p-14 border border-brand-brown/5">
            <div className="absolute -bottom-14 -right-14 h-52 w-52 rounded-full bg-brand-bone/40 blur-3xl" aria-hidden />
            <div className="relative max-w-3xl">
              <div className="h-14 w-14 rounded-full bg-brand-bone flex items-center justify-center mb-6">
                <Users size={22} className="text-brand-brown" strokeWidth={1.5} />
              </div>
              <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">Why our groups are small</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{INVESTMENT.small_groups.heading}</h3>
              <p className="mt-6 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
                {INVESTMENT.small_groups.body}
              </p>
              <p className="mt-5 font-serif italic text-lg text-brand-brown-light">
                {INVESTMENT.small_groups.clinical}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* INVESTING IN YOURSELF */}
      <section className="py-8">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-start">
            <div className="md:mt-1">
              <div className="h-14 w-14 rounded-full bg-brand-bone flex items-center justify-center">
                <Sparkles size={22} className="text-brand-brown" strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">Beyond an online course</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{INVESTMENT.investing_in_yourself.heading}</h3>
              <p className="mt-5 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed max-w-3xl">
                {INVESTMENT.investing_in_yourself.intro}
              </p>
              <ul className="mt-7 grid sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl">
                {INVESTMENT.investing_in_yourself.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-3 font-sans text-base text-brand-brown-light/95">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-gold mt-2.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-7 font-serif italic text-lg text-brand-brown-light max-w-3xl leading-relaxed">
                {INVESTMENT.investing_in_yourself.intention}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ACCESSIBILITY */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div data-testid="investment-accessibility" className="bg-brand-bg-soft rounded-3xl p-10 md:p-12 border border-brand-brown/5">
            <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-start">
              <div>
                <div className="h-14 w-14 rounded-full bg-brand-sage flex items-center justify-center">
                  <HandHeart size={22} className="text-brand-brown" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <p className="font-sans text-[11px] uppercase tracking-[0.28em] text-brand-brown-muted mb-3">Held with care</p>
                <h3 className="font-serif text-3xl md:text-4xl text-brand-brown leading-tight">{INVESTMENT.accessibility.heading}</h3>
                <div className="mt-6 space-y-4 font-sans text-base md:text-lg text-brand-brown-light/95 leading-relaxed">
                  {INVESTMENT.accessibility.body.map((line, i) => <p key={i}>{line}</p>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-20 md:py-24 bg-brand-bg-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="divider-leaf mb-8" />
          <p className="font-serif italic text-xl md:text-2xl text-brand-brown-light leading-relaxed">
            {CLOSING_STATEMENT.body}
          </p>
          <p className="mt-8 font-serif italic text-xl md:text-2xl text-brand-gold tracking-wide">
            {CLOSING_STATEMENT.strapline}
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/booking"
              data-testid="investment-cta-intake"
              className="font-sans text-sm tracking-wide bg-brand-brown text-brand-bg rounded-full px-8 py-3.5 hover:bg-brand-brown-light transition-colors duration-500"
            >
              Book a Complimentary Conversation
            </Link>
            <Link
              to="/booking"
              data-testid="investment-cta-apply"
              className="font-sans text-sm tracking-wide border border-brand-brown text-brand-brown rounded-full px-8 py-3.5 hover:bg-brand-brown hover:text-brand-bg transition-colors duration-500"
            >
              Apply for Path I
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
